import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_THEME_KEY = "outlaw_theme";
const STORAGE_ACCENT_KEY = "outlaw_accent";

const ThemeContext = createContext(null);

function readStoredValue(key, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => readStoredValue(STORAGE_THEME_KEY, "dark"));
  const [accent, setAccent] = useState(() => readStoredValue(STORAGE_ACCENT_KEY, "blue"));

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_THEME_KEY, theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_ACCENT_KEY, accent);
    } catch {}
  }, [accent]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme === "light" ? "light" : "dark";
    root.dataset.accent = accent || "blue";
  }, [theme, accent]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      accent,
      setAccent,
      toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    }),
    [theme, accent]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
